import { KgInsightThemeType } from '@api';
import { THEME_FILL_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import clsx from 'clsx';
import {
  isMentionType,
  Mention,
  MentionCandidate,
  MentionType,
  mentionCandidateToMention,
} from '../types';
import { cloneDesignSystemIcon } from './dom-icon';
import styles from './MentionComposerInput.module.scss';

const MENTION_ATTR = 'data-mention-chip';
const TYPE_ATTR = 'data-mention-type';
const ID_ATTR = 'data-mention-id';
const LABEL_ATTR = 'data-mention-label';

export const CHIP_SELECTED_CLASS = styles.chipSelected;

export const isMentionChipElement = (
  node: Node | null | undefined
): node is HTMLElement =>
  node instanceof HTMLElement && node.getAttribute(MENTION_ATTR) === '1';

const kindClassByType: Record<MentionType, string> = {
  entity: styles.chipEntity,
  theme: styles.chipTheme,
  list: styles.chipWatchlist,
  'public-list': styles.chipWatchlist,
};

const createWatchlistGlyph = (): HTMLElement => {
  const wrap = document.createElement('span');
  wrap.className = styles.chipWatchlistGlyphWrap;
  wrap.appendChild(cloneDesignSystemIcon('Watchlist', 8, styles.chipGlyph));
  return wrap;
};

const createLetterInitial = (
  label: string,
  fillClass?: string
): HTMLElement => {
  const initial = document.createElement('span');
  initial.className = clsx(
    styles.chipAvatarInitial,
    fillClass ?? styles.chipAvatarInitialEntity
  );
  initial.setAttribute('aria-hidden', 'true');
  initial.textContent = label.trim().charAt(0).toUpperCase() || '?';
  return initial;
};

const createAvatarGlyph = (
  label: string,
  logoUrl: string | undefined,
  fillClass?: string
): HTMLElement => {
  if (!logoUrl) {
    return createLetterInitial(label, fillClass);
  }

  const img = document.createElement('img');
  img.className = clsx(
    fillClass ? styles.chipThemeAvatar : styles.chipAvatar,
    fillClass
  );
  img.setAttribute('alt', '');
  img.src = logoUrl;
  return img;
};

const resolveLogoUrl = (candidate: MentionCandidate): string | undefined => {
  if (candidate.kind === 'entity') {
    return candidate.entity.logo_urls.light_png;
  }
  if (candidate.kind === 'theme') {
    return candidate.theme.logo_url;
  }
  return undefined;
};

const createGlyph = (
  mention: Mention,
  logoUrl?: string,
  themeType?: KgInsightThemeType
): HTMLElement | SVGSVGElement => {
  if (mention.type === 'entity') {
    return createAvatarGlyph(mention.label, logoUrl);
  }
  if (mention.type === 'theme') {
    return createAvatarGlyph(
      mention.label,
      logoUrl,
      THEME_FILL_COLOR_CLASS_MAP[themeType ?? 'industry']
    );
  }
  return createWatchlistGlyph();
};

const assembleChip = (
  mention: Mention,
  glyph: HTMLElement | SVGSVGElement
): HTMLElement => {
  const chip = document.createElement('span');
  chip.setAttribute(MENTION_ATTR, '1');
  chip.setAttribute(TYPE_ATTR, mention.type);
  chip.setAttribute(ID_ATTR, mention.id);
  chip.setAttribute(LABEL_ATTR, mention.label);
  chip.setAttribute('contenteditable', 'false');
  chip.className = `${styles.chip} ${kindClassByType[mention.type]}`;

  const label = document.createElement('span');
  label.textContent = mention.label;
  chip.appendChild(glyph);
  chip.appendChild(label);
  return chip;
};

export const createMentionChipElement = (
  candidate: MentionCandidate
): HTMLElement => {
  const mention = mentionCandidateToMention(candidate);
  const themeType =
    candidate.kind === 'theme' ? candidate.theme.type : undefined;
  return assembleChip(
    mention,
    createGlyph(mention, resolveLogoUrl(candidate), themeType)
  );
};

export const createMentionChipFromMention = (mention: Mention): HTMLElement =>
  assembleChip(mention, createGlyph(mention));

export const readMentionFromChipElement = (el: HTMLElement): Mention => {
  const type = el.getAttribute(TYPE_ATTR) ?? '';
  const label = el.getAttribute(LABEL_ATTR) ?? '';

  return {
    type: isMentionType(type) ? type : 'entity',
    id: el.getAttribute(ID_ATTR) ?? '',
    name: label,
    label,
  };
};
